import React, { useState, useEffect, useCallback } from "react";
import PageContent from "../../layout/PageContent";
import {IntegrationInstance, useInstallsPageGetLazyQuery, useInstallsPageReInitMutation} from "../../Queries";
import {useNavigate} from "react-router";
import {ShopIntegrationSettingsPath} from "../settings/details/ShopIntegrationSettings";
import {BrandIntegrationSettingsPath} from "../settings/details/BrandIntegrationSettings";
import {LogisticsIntegrationPagePath} from "../logistics/LogisticsIntegrationPage";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Button
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

type InstallsPageProps = {
   connectorId: string
}

export const InstallsPage = ({connectorId}: InstallsPageProps) => {
   const navigate = useNavigate();
   const [instances, setInstances] = useState<IntegrationInstance[]>([]);
   const [messages, setMessages] = useState<string[]>([]);
   const [loading, setLoading] = useState(true);
   const [reInitLoading, setReInitLoading] = useState<Set<string>>(new Set());
   const [reInitDone, setReInitDone] = useState<Set<string>>(new Set());

   const [requestData] = useInstallsPageGetLazyQuery({
      variables: {
         connectorId
      }
   });

   const [reInit] = useInstallsPageReInitMutation();

   const handleReInit = async (e: React.MouseEvent, instanceId: string) => {
      e.stopPropagation();
      setReInitLoading(prev => new Set(prev).add(instanceId));
      await reInit({ variables: { instanceId } });
      setReInitLoading(prev => {
         const next = new Set(prev);
         next.delete(instanceId);
         return next;
      });
      setReInitDone(prev => new Set(prev).add(instanceId));
   };

   const loadData = useCallback(async () => {
      setLoading(true);
      try {
         const {data} = await requestData({
            variables: {
               connectorId: connectorId
            }
         });

         const allData = data?.IntegrationInstances || [];
         setInstances(allData as IntegrationInstance[]);

         // Get messages from the first instance's connector (they're all the same)
         const connectorMessages = allData[0]?.connector?.messages || [];
         setMessages(connectorMessages.filter((m): m is string => m !== null));
      } catch (error) {
         console.error('Error loading instances:', error);
      } finally {
         setLoading(false);
      }
   }, [connectorId, requestData]);

   useEffect(() => {
      loadData();
   }, [loadData]);

   const getName = (instance: IntegrationInstance) => {
      return instance?.refShop?.name || instance?.refBrandHouse?.name || instance?.refLogisticsPartner?.name || 'Unknown';
   };

   const isMessageEnabled = (instance: IntegrationInstance, message: string) => {
      const config = instance.configs?.find(c => c?.message === message);
      return config?.enabled === true;
   };

   const handleRowClick = (instance: IntegrationInstance) => {
      if (instance.refShop?.brandHouseId) {
         navigate(ShopIntegrationSettingsPath(instance.refShop?.brandHouseId, instance.refShop?._id, instance._id));
      } else if (instance.refLogisticsPartner?._id) {
         navigate(LogisticsIntegrationPagePath(instance.refLogisticsPartner?._id, instance._id));
      } else {
         navigate(BrandIntegrationSettingsPath(instance.refBrandHouse?._id, instance._id));
      }
   };

   if (loading) {
      return (
         <PageContent>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
               <CircularProgress />
            </Box>
         </PageContent>
      );
   }

   return (
      <PageContent>
         <TableContainer component={Paper} sx={{ maxHeight: '80vh' }}>
            <Table stickyHeader sx={{ minWidth: 650 }} aria-label="integration instances table">
               <TableHead>
                  <TableRow>
                     <TableCell>Name</TableCell>
                     {messages.map(message => (
                        <TableCell key={message} title={message}>{message.slice(0, 3)}</TableCell>
                     ))}
                     <TableCell>Target</TableCell>
                     <TableCell></TableCell>
                  </TableRow>
               </TableHead>
               <TableBody>
                  {instances.map((instance) => (
                     <TableRow
                        key={instance._id}
                        onClick={() => handleRowClick(instance)}
                        sx={{
                           cursor: 'pointer',
                           '&:hover': {
                              backgroundColor: 'action.hover'
                           }
                        }}
                     >
                        <TableCell>{getName(instance)}</TableCell>
                        {messages.map(message => (
                           <TableCell key={message}>
                              {isMessageEnabled(instance, message) && <CheckIcon color="success" />}
                           </TableCell>
                        ))}
                        <TableCell>{instance.refType}</TableCell>
                        <TableCell>
                           {reInitDone.has(instance._id) ? (
                              <CheckIcon color="success" />
                           ) : reInitLoading.has(instance._id) ? (
                              <CircularProgress size={24} />
                           ) : (
                              <Button
                                 variant="outlined"
                                 color="secondary"
                                 size="small"
                                 onClick={(e) => handleReInit(e, instance._id)}
                              >
                                 RE-INIT
                              </Button>
                           )}
                        </TableCell>
                     </TableRow>
                  ))}
               </TableBody>
            </Table>
         </TableContainer>
      </PageContent>
   );
}